'use client';
import React, { useState, useEffect, useMemo } from "react";
import ValidateConfirmCode from '../Services/ValidateConfirmCode';
import LocationPermissionGranted from '../Services/LocationService';
import { setCookie } from 'typescript-cookie';
import { useRouter } from 'next/navigation';
// Models
import { CashOutModel } from '../Models/CashOutModel';
import { PersonalInfoModel, validatePersonalInfo, defaultPersonalInfoModel, PersonalInfoErrors } from "../Models/PersonalInfoModel";
import { defaultLocationInfoModel, LocationInfoErrors, LocationInfoModel, validateLocationInfo } from "../Models/LocationInfoModel";
import { defaultGCUsageModel, GCUsageErrors, GCUsageModel, validateGCUsage } from "../Models/GCUsageModel";
import { defaultGCInfoModel, GCInfoErrors, GCInfoModel, validateGCInfo } from "../Models/GCInfoModel";
import { GeocodingModel } from "../Models/GeocodingModel";
import { ConfirmCode, initializedConfirmCode } from '../Models/ConfirmCode';
// Controls
import { Typography, Box, Stack, Button, Switch, FormControlLabel, Alert, AlertTitle, Collapse, TextField,
        Dialog, DialogContent, DialogContentText, DialogTitle, DialogActions, List, ListItem, CircularProgress } from "@mui/material";
import LegalTerms from "../Components/Legal/LegalTerms";
import PersonalCard from "../Components/Cards/PersonalCard";
import LocationCard from "../Components/Cards/LocationCard";
import GCInfoCard from "../Components/Cards/GCInfoCard";
import GCUsageCard from "../Components/Cards/GCUsageCard";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
// Icons
import SendIcon from '@mui/icons-material/Send';
import FooterInfo from "../Components/Footer/FooterInfo";
import HeaderInfo from "../Components/Header/HeaderInfo";
import { CardSiteDataModel } from "../Models/CardSiteDataModel";
import { CashOutStateModel } from "../Models/CashOutStateModel";

const CashOutPage = () => {
    const router = useRouter();
    const [referringUrl, setReferringUrl] = useState('');
    const [cardSiteData, setCardSiteData] = useState<CardSiteDataModel | null>(null);
    const [cashOutStates, setCashOutStates] = useState<CashOutStateModel[]>([]);
    const [pageLoading, setPageLoading] = useState(true);

    useEffect(() => {
        const referringUrl = document.referrer;
        setReferringUrl(referringUrl);

        const payload = { referringUrl };

        const loadData = async () => {
            try {
                const [cardSitesRes, statesRes] = await Promise.all([
                    fetch("/api/getCardSiteData", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                    }),
                    fetch("/api/getCashOutStates", {
                    method: "POST",
                    headers: { "Content-Type": "application/json"}
                    })
                ]);
                    
                // ---- Handle getCardSitesData ----
                if (!cardSitesRes.ok) {
                    const text = await cardSitesRes.text().catch(() => "");
                    console.log(`getCardSiteData failed: ${cardSitesRes.status} / ${cardSitesRes.statusText}\n${text}`);
                    router.push("/NavigationError");
                    return;
                }

                const cardSitesData = await cardSitesRes.json() as CardSiteDataModel;
                setCardSiteData(cardSitesData);

                // ---- Handle getCashOutStates ----
                if (!statesRes.ok) {
                    const text = await statesRes.text().catch(() => "");
                    console.log(`getCashOutStates failed: ${statesRes.status} / ${statesRes.statusText}\n${text}`);
                    router.push("/NavigationError");
                    return;
                }

                const statesData = await statesRes.json() as CashOutStateModel[]
                setCashOutStates(statesData);
            } catch (e) {
                router.push("/NavigationError");
                return;
            } finally {
                setPageLoading(false);
            }
        };

        loadData();

        setUserAgent(navigator.userAgent);
        setEmailOpen(true);
    }, []);


    const [emailDialogOpen, setEmailOpen] = useState<boolean>(false);
    const [errorDialogOpen, setErrorOpen] = useState<boolean>(false);
    const [email, setEmail] = useState<string>('');
    const [emailErrorText, setErrorText] = useState<string>('');

    const [confirmCode, setConfirmCode] = useState<string>('');
    const [confirmCodeObj, setConfirmCodeObj] = useState<ConfirmCode>(initializedConfirmCode());
    const [confirmDialogOpen, setConfirmOpen] = useState<boolean>(false);

    const backConfirmDialog = () => {
        setConfirmOpen(false);
        setEmailOpen(true);
    }

    const [personalData, setPersonalData] = useState<PersonalInfoModel>(defaultPersonalInfoModel(''));
    const [personalErrors, setPersonalErrors] = useState<PersonalInfoErrors>({
        firstNameError: false,
        lastNameError: false,
        emailError: false,
        phoneNumberError: false,
        dateOfBirthError: false,
        payoutMethodError: false,
        payoutIdError: false,
        errors: []
    });
    
    const [locationInfo, setLocationInfo] = useState<LocationInfoModel>(defaultLocationInfoModel());
    const [locationErrors, setLocationErrors] = useState<LocationInfoErrors>({
        addressError: false,
        cityError: false,
        stateError: false,
        zipError: false,
        errors: []
    });

    const [gcInfo, setGCInfo] = useState<GCInfoModel>(defaultGCInfoModel(''));
    const [gcInfoErrors, setGCInfoErrors] = useState<GCInfoErrors>({
        giftCardNumberError: false,
        giftCardPinError: false,
        issuerNameError: false,
        brandNameError: false,
        cashOutAmountError: false,
        cardPurchasedDateError: false,
        beginningCardBalanceError: false,
        errors: []
    });

    // set selectedCashOutState based on locationInfo.state & gcInfo.cardPurchaseDate selection
    const selectedCashOutState = useMemo(() => {
        const us = locationInfo.state;
        if (!us) return null;

        const sameState = cashOutStates.filter(
            s => s.stateCode.toUpperCase() === us.value.toUpperCase()
        );

        if (sameState.length === 0) return null;

        if (!gcInfo.cardPurchasedDate) {
            return [...sameState]
                .sort((a, b) => 
                    dayjs(b.statuteEffectiveDate).valueOf() - dayjs(a.statuteEffectiveDate).valueOf()
            )[0];
        }

        const purchasedDate = gcInfo.cardPurchasedDate;

        const filteredStates = cashOutStates.filter(s =>
            s.stateCode.toUpperCase() === us.value.toUpperCase() &&
            (purchasedDate.isSame(dayjs(s.statuteEffectiveDate)) ||
            purchasedDate.isAfter(dayjs(s.statuteEffectiveDate))) 
        )
       
        if (filteredStates.length === 0) return null;

        return filteredStates.sort(
            (a, b) => dayjs(b.statuteEffectiveDate).valueOf() - dayjs(a.statuteEffectiveDate).valueOf()
        )[0];
    }, [locationInfo.state, gcInfo.cardPurchasedDate, cashOutStates]);

    const [gcUsage, setGCUsage] = useState<GCUsageModel>(defaultGCUsageModel());
    const [gcUsageErrors, setGCUsageErrors] = useState<GCUsageErrors>({
        cardLastUsedDateError: false,
        stateLastUsedError: false,
        errors: []
    });

    const [bannerOpen, setBannerOpen] = useState<boolean>(true);
    const [noticeOpen, setNoticeOpen] = useState<boolean>(true);
    const [userAgent, setUserAgent] = useState<string>('');
    const [lat, setLat] = useState<number>(0);
    const [long, setLong] = useState<number>(0);
    const [requestAddress, setReqAddr] = useState<string>('');
    const [openErrorDialog, setOpenErrorDialog] = useState<boolean>(false);
    const [isValid, setIsValid] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const redirectComplete = async () => {
        router.push("/RequestComplete");
    }

    const submitCashOutRequest = async () => {
        if(!(await runValidation())) {
            return;
        }

        try {
            const granted = await LocationPermissionGranted(navigator, personalData.email);
            if (granted) {
                navigator.geolocation.getCurrentPosition(
                    success,
                    geoError,
                    { timeout: 5000, enableHighAccuracy: false, maximumAge: 0 }
                );
            } else {
                console.log('geolocation permission not granted.');
            }
        } catch (err) {
            console.error('[submitCashOutRequest] LocationPerimissionGranted threw:', err);
        }

        setIsLoading(true);
        let fullName = "";
        if (personalData.middleName) {
            fullName = `${personalData.firstName} ${personalData.middleName} ${personalData.lastName}`
        }
        else {
            fullName = `${personalData.firstName} ${personalData.lastName}`
        }

        dayjs.extend(utc);

        let cashOutModel: CashOutModel = {
            fullName: fullName,
            firstName: personalData.firstName,
            middleName: personalData.middleName,
            lastName: personalData.lastName,
            email: personalData.email,
            phoneNumber: personalData.phoneNumber,
            dateOfBirth: personalData.dateOfBirth ? personalData.dateOfBirth.format('YYYY-MM-DD') : '',
            payoutMethod: personalData.payoutMethod,
            payoutId: personalData.payoutId,
            address: locationInfo.address,
            city: locationInfo.city,
            state: locationInfo.state ? locationInfo.state.label : '',
            zip: locationInfo.zip,
            giftCardNumber: gcInfo.giftCardNumber,
            giftCardPin: gcInfo.giftCardPin,
            issuerName: gcInfo.issuerName,
            brandName: gcInfo.brandName,
            cashOutAmount: gcInfo.cashOutAmount,
            cardPurchasedDate: gcInfo.cardPurchasedDate ? gcInfo.cardPurchasedDate.format('YYYY-MM-DD') : null,
            beginningCardBalance: gcInfo.beginningCardBalance,
            cardLastUsedDate: gcUsage.cardLastUsedDate ? gcUsage.cardLastUsedDate.format('YYYY-MM-DD') : null,
            stateLastUsed: gcUsage.stateLastUsed ? gcUsage.stateLastUsed.label : '',
            requestDate: dayjs().utc().format('YYYY-MM-DD HH:mm:ss'),
            country: 'USA',
            lat: lat,
            long: long,
            userAgent: userAgent,
            requestAddress: requestAddress,
            referringUrl: referringUrl
        };
        
        (async () => {
            try {
                const response = await fetch("/api/CashOutService", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(cashOutModel),
                });

                if (response.ok) {
                    redirectComplete();
                    return;
                } else {
                    setOpenErrorDialog(true);
                    setIsLoading(false);
                    cleanUp();
                    return;
                }              
            } catch (e) {
                if (typeof e === "string") {
                    let message = `CashOut: Error calling CashOutService:\n${e}`;
                    console.log(message);
                }
                else if (e instanceof Error) {
                    console.log(e.message);
                }
                setIsLoading(false);
                cleanUp();
                return;
            }
        })();
    }

    const success = (pos : any) => {
        var crd = pos.coords;
        setLat(crd.latitude);
        setLong(crd.longitude);

        let geocodingRequest: GeocodingModel = {
            lat: crd.latitude,
            long: crd.longitude
        };

        (async () => {
            try {
                const response = await fetch("/api/ReverseGeocoding", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify(geocodingRequest)
                });
                const text = await response.text();

                let data;
                try { data = JSON.parse(text); } catch { data = { formatted_address: text }; }

                if (!response.ok) {
                    setReqAddr('Error retrieving request address.');
                    return;
                }
                setReqAddr(data.formatted_address);
                return;
            } catch (e) {
                setReqAddr('Error retrieving request address.');
            }
        }) ();
    };

    const geoError = (err: GeolocationPositionError) => {
        // GeolocationPositionError codes:
        // 1 = PERMISSION_DENIED
        // 2 = POSITION_UNAVAILABLE
        // 3 = TIMEOUT
        console.warn(`[Geolocation Error] code=${err.code} message=${err.message}`);
    };

    const cleanUp = () => {

        setPersonalData(defaultPersonalInfoModel(email));
        setLocationInfo(defaultLocationInfoModel());
        setGCInfo(defaultGCInfoModel(gcInfo.issuerName));
        setGCUsage(defaultGCUsageModel());

        setBannerOpen(true);
        setNoticeOpen(true);
        setIsValid(true);
    };

    const runValidation = async (): Promise<boolean> => {

        let pErrors = await validatePersonalInfo(personalData);
        let lErrors = validateLocationInfo(locationInfo);
        let gcIErrors = validateGCInfo(gcInfo);
        let gcUErrors = validateGCUsage(gcUsage);

        setPersonalErrors(pErrors);
        setLocationErrors(lErrors);
        setGCInfoErrors(gcIErrors);
        setGCUsageErrors(gcUErrors);

        let numErrors = pErrors.errors.length + lErrors.errors.length + gcIErrors.errors.length + gcUErrors.errors.length;

        if (numErrors > 0) {
            setIsValid(false);
            return false;
        } else {
            setIsValid(true);
            return true;
        }
    }

    if (pageLoading) return <p>Loading...</p>;

    return (
        <div>
            <HeaderInfo />
            <div style={{marginTop: 80}}>
                <Box m={3} component="form"
                    sx={{
                        '& .MuiTextField-root': { m: 1, width: '40ch' },
                    }}>

                    <Stack spacing={1}>
                        <Dialog
                            open={emailDialogOpen}
                            PaperProps={{
                                component: 'form',
                                onSubmit: async (event: React.FormEvent<HTMLFormElement>) => {
                                    event.preventDefault();
                                    (async () => {
                                        try {
                                            const response = await fetch("/api/SendEmail", {
                                                method: "POST",
                                                headers: {
                                                    "Content-Type": "application/json"
                                                },
                                                body: JSON.stringify(email)
                                            });

                                            if (!response.ok) {
                                                setErrorText("The email address provided is not valid");
                                                setErrorOpen(true);
                                                return;
                                            }

                                            const data = await response.json();
                                            const confirmCode: ConfirmCode = data.confirmCode;
                                            setConfirmCodeObj(prevCode => {
                                                return {...prevCode, id: prevCode.id = confirmCode.id, value: prevCode.value = confirmCode.value }
                                            });
                                            setCookie(`${process.env.CASH_OUT_CONFIRM_CODE}`, confirmCode.id, { expires: 0.5 })
                                            setEmailOpen(false);
                                            personalData.email = email;
                                            setConfirmOpen(true);
                                        } catch (e) {
                                            if (typeof e === "string") {
                                                let message = `CashOut: Error calling SendEmail service:\n${e}`;
                                                console.log(message);
                                            }
                                            else if (e instanceof Error) {
                                                console.log(e.message);
                                            }
                                            setErrorText("The email address provided could not be validated.");
                                            setErrorOpen(true);
                                        }
                                    }) ();
                                }
                            }}
                        >
                            <DialogTitle>Confirm Email</DialogTitle>
                            <DialogContent>
                                <DialogContentText>
                                    To continue with your request please enter a valid email. You will be sent a confirmation code to gain access to this form.
                                </DialogContentText>
                                <TextField
                                    autoFocus
                                    required
                                    margin="dense"
                                    id="email"
                                    label="Email Address"
                                    type="email"
                                    fullWidth
                                    variant="standard"
                                    onChange={(event) => { setEmail(event.target.value) }}
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button type="submit">Submit</Button>
                            </DialogActions>
                        </Dialog>

                        <Dialog
                            open={confirmDialogOpen}
                            PaperProps={{
                                component: 'form',
                                onSubmit: async (event: React.FormEvent<HTMLFormElement>) => {
                                    event.preventDefault();
                                    if (await ValidateConfirmCode(confirmCodeObj, confirmCode)) {
                                        setConfirmOpen(false);
                                    }
                                    else {
                                        setErrorText("The confirmation code provided is not valid");
                                        setErrorOpen(true);
                                    }
                                }
                            }}
                        >
                            <DialogTitle>Enter Confirm Code</DialogTitle>
                            <DialogContent>
                                <DialogContentText>
                                    Please check your email and enter the code you received below.  The code will be invalid after 12 hours.
                                </DialogContentText>
                                <TextField
                                    autoFocus
                                    required
                                    margin="dense"
                                    id="confirm_code"
                                    label="Confirmation Code"
                                    type="text"
                                    fullWidth
                                    variant="standard"
                                    onChange={(event) => { setConfirmCode(event.target.value) }}
                                />
                                <DialogActions>
                                    <Button type="button" onClick={backConfirmDialog}>Go Back</Button>
                                    <Button type="submit">Submit</Button>
                                </DialogActions>
                            </DialogContent>
                        </Dialog>

                        <Dialog
                            open={errorDialogOpen}
                        >
                            <DialogTitle>Error</DialogTitle>
                            <DialogContent>
                                <DialogContentText>
                                    {emailErrorText}
                                </DialogContentText>
                                <DialogActions>
                                    <Button type="button" onClick={() => {setErrorOpen(false)}}>Close</Button>
                                </DialogActions>
                            </DialogContent>
                        </Dialog>

                        <Collapse in={noticeOpen}>
                            <Alert variant="filled" severity="info" onClose={() => {setNoticeOpen(false)}}>
                                <AlertTitle>Important Notice</AlertTitle>
                                You are not entitled to cash-out your gift card unless cash redemptions are
                                required by law. Therefore, the card issuer reserves its right to deny the cash-out request.
                            </Alert>
                        </Collapse>

                        <Collapse in={!isValid}>
                            <Alert variant="outlined" severity="error"
                                action={
                                    <Button onClick={runValidation} color="inherit" size="small">Re-Validate</Button>
                                }>
                                <AlertTitle>Form Errors</AlertTitle>
                                <List>
                                    {personalErrors.errors.map((error, index) => {
                                        return <ListItem key={index}>{error}</ListItem>
                                    })}
                                    {locationErrors.errors.map((error, index) => {
                                        return <ListItem key={index + 100}>{error}</ListItem>
                                    })}
                                    {gcInfoErrors.errors.map((error, index) => {
                                        return <ListItem key={index + 200}>{error}</ListItem>
                                    })}
                                    {gcUsageErrors.errors.map((error, index) => {
                                        return <ListItem key={index + 300}>{error}</ListItem>
                                    })}
                                </List>
                            </Alert>
                        </Collapse>

                        <PersonalCard
                            data={personalData}
                            setPersonalData={setPersonalData}
                            errors={personalErrors}
                        />

                        <LocationCard
                            data={locationInfo}
                            setData={setLocationInfo}
                            errors={locationErrors}
                        />

                        <GCInfoCard
                            data={gcInfo}
                            setData={setGCInfo}
                            cardSiteData={cardSiteData}
                            errors={gcInfoErrors}
                        />

                        <GCUsageCard
                            data={gcUsage}
                            setData={setGCUsage}
                            cardSiteData={cardSiteData}
                            cashOutState={selectedCashOutState}
                            errors={gcUsageErrors}
                        />

                        <Button
                            sx={{ maxWidth: 250 }}
                            variant="contained"
                            color="secondary"
                            size="large"
                            onClick={submitCashOutRequest}
                            disabled={bannerOpen || isLoading}
                            endIcon={<SendIcon />}>
                            {isLoading ? (
                                <Box paddingRight={3} height={20} width={5}>
                                    <CircularProgress color="inherit" size={20}/>
                                </Box>
                            ) : null}
                                Request Cash Out
                        </Button>

                        <FormControlLabel
                            label="Show Legal Terms"
                            control={<Switch
                            checked={bannerOpen}
                            onChange={(e) => setBannerOpen(e.target.checked)}
                            name="accept-terms-switch" />}
                        />
                    </Stack>
                </Box>
            </div>
            <FooterInfo />
            <LegalTerms
                bannerOpen={bannerOpen}
                setBannerOpen={setBannerOpen}
                setBannerClosed={() => {setBannerOpen(false)}} />

            <Dialog open={openErrorDialog} onClose={() => {setOpenErrorDialog(false)}}>
                <DialogTitle>
                    {"Request Failed"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        There was an error submitting your request. Please try again at a later time.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {setOpenErrorDialog(false)}} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default CashOutPage;